import React, { useState } from 'react';
import axios from 'axios';
import { QrCodeIcon, CurrencyEuroIcon, CheckCircleIcon, UserIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { QrReader } from 'react-qr-reader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FidelityScanner = () => {
  const { token } = useAuth();
  const [scanMode, setScanMode] = useState('manual'); // 'manual' or 'camera'
  const [customerCode, setCustomerCode] = useState('');
  const [customer, setCustomer] = useState(null);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  const startCamera = () => {
    setScanMode('camera');
    toast.success('Scanner QR avviato');
  };

  const stopCamera = () => {
    setScanMode('manual');
  };

  const handleScan = (result, error) => {
    if (result) {
      const qrCode = result?.text;
      if (qrCode && qrCode.length > 0) {
        console.log('QR Code scansionato:', qrCode);
        
        // Ferma lo scanner
        setScanMode('manual');
        
        // Cerca il cliente con il codice scansionato
        setCustomerCode(qrCode);
        searchCustomer(qrCode);
        
        toast.success('QR Code letto con successo!');
      }
    }
    
    if (error) {
      // Ignora errori normali di scansione (quando non trova un QR)
      console.log('Scanner in attesa di QR code...');
    }
  };

  const searchCustomer = async (code) => {
    if (!code) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/fidelity/customers/${code}`);
      setCustomer(response.data);
      toast.success(`Cliente trovato: ${response.data.name} ${response.data.surname}`);
    } catch (error) {
      console.error('Errore ricerca cliente:', error);
      toast.error('Cliente non trovato. Verifica il codice carta.');
      setCustomer(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    searchCustomer(customerCode);
  };

  const handleAddPoints = async (e) => {
    e.preventDefault();
    
    if (!customer) {
      toast.error('Nessun cliente selezionato');
      return;
    }

    const euros = parseFloat(amount);
    if (isNaN(euros) || euros <= 0) {
      toast.error('Inserisci un importo valido');
      return;
    }

    const points = Math.floor(euros); // 1€ = 1 punto

    setProcessing(true);
    try {
      await axios.post(
        `${API}/fidelity/points/add`,
        {
          customer_code: customer.customer_code,
          amount: points,
          description: description || `Acquisto €${euros.toFixed(2)}`
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(`${points} punti accreditati con successo!`);
      
      // Reset
      setAmount('');
      setDescription('');
      setCustomer(null);
      setCustomerCode('');
      
      // Reload customer to show new balance
      await searchCustomer(customer.customer_code);
    } catch (error) {
      console.error('Errore accredito punti:', error);
      toast.error('Errore nell\'accredito dei punti');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Scanner Carte Fidelity</h1>
        <p className="text-gray-600">Scansiona la carta cliente e accredita punti</p>
      </div>

      {/* Mode Selection */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <Button
              variant={scanMode === 'manual' ? 'default' : 'outline'}
              onClick={() => {
                stopCamera();
                setScanMode('manual');
              }}
              className="flex-1"
            >
              Input Manuale
            </Button>
            <Button
              variant={scanMode === 'camera' ? 'default' : 'outline'}
              onClick={startCamera}
              className="flex-1"
            >
              <QrCodeIcon className="w-4 h-4 mr-2" />
              Scanner QR
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Scanner / Input */}
      <Card>
        <CardHeader>
          <CardTitle>
            {scanMode === 'manual' ? 'Inserisci Codice Carta' : 'Scansiona Codice QR'}
          </CardTitle>
          <CardDescription>
            {scanMode === 'manual' 
              ? 'Inserisci il codice numerico della carta cliente'
              : 'Posiziona il codice QR davanti alla camera'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {scanMode === 'manual' ? (
            <form onSubmit={handleCodeSubmit} className="space-y-4">
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="es. 1000000001"
                  value={customerCode}
                  onChange={(e) => setCustomerCode(e.target.value)}
                  className="flex-1"
                  pattern="[0-9]*"
                />
                <Button type="submit" disabled={loading || !customerCode}>
                  {loading ? 'Ricerca...' : 'Cerca'}
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              {/* QR Scanner Container */}
              <div className="qr-scanner-container relative rounded-lg overflow-hidden bg-gray-900" style={{ minHeight: '400px' }}>
                <QrReader
                  onResult={handleScan}
                  constraints={{ 
                    facingMode: 'environment',
                    aspectRatio: 1 
                  }}
                  videoId="qr-video"
                  scanDelay={300}
                  className="qr-reader"
                />
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-800">
                <p className="font-medium mb-1">📱 Come usare lo scanner:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Autorizza l'uso della fotocamera quando richiesto</li>
                  <li>Posiziona il codice QR davanti alla fotocamera</li>
                  <li>Mantieni stabile finché non viene rilevato</li>
                  <li>Il sistema cercherà automaticamente il cliente</li>
                </ol>
                <Button
                  onClick={stopCamera}
                  variant="outline"
                  className="mt-3 w-full"
                  size="sm"
                >
                  <XMarkIcon className="w-4 h-4 mr-2" />
                  Ferma Scanner
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer Info */}
      {customer && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <UserIcon className="w-5 h-5 text-green-600" />
                  {customer.name} {customer.surname}
                </CardTitle>
                <CardDescription>Carta N° {customer.customer_code}</CardDescription>
              </div>
              <Badge className="bg-green-600 text-white">
                Saldo: {customer.points_balance} punti
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Email:</span>
                <p className="font-medium">{customer.email}</p>
              </div>
              <div>
                <span className="text-gray-600">Telefono:</span>
                <p className="font-medium">{customer.phone}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add Points Form */}
      {customer && (
        <Card>
          <CardHeader>
            <CardTitle>Accredita Punti</CardTitle>
            <CardDescription>
              Inserisci l'importo dell'acquisto (1€ = 1 punto)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAddPoints} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Importo Acquisto (€) *</Label>
                <div className="relative">
                  <CurrencyEuroIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="50.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="pl-10"
                    required
                  />
                </div>
                {amount && (
                  <p className="text-sm text-green-600 font-medium">
                    → Verranno accreditati {Math.floor(parseFloat(amount) || 0)} punti
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrizione (opzionale)</Label>
                <Input
                  id="description"
                  type="text"
                  placeholder="es. Acquisto bottiglie"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

              <Button
                type="submit"
                disabled={processing || !amount}
                className="w-full"
                size="lg"
              >
                <CheckCircleIcon className="w-5 h-5 mr-2" />
                {processing ? 'Accredito in corso...' : 'Accredita Punti'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {!customer && (
        <Card>
          <CardContent className="py-16 text-center">
            <QrCodeIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nessun cliente selezionato
            </h3>
            <p className="text-sm text-gray-600">
              Scansiona o inserisci manualmente il codice carta per iniziare
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FidelityScanner;
